import { FC, useCallback, useEffect, useRef, useState } from "react";
import { z, ZodError } from "zod";
import { PageProps } from "shared/defines";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import "react-toastify/dist/ReactToastify.css";
import Button from "../shared/components/buttons";
import { Input } from "../shared/components/input";
import { emailState } from "../state/parent-details";
import { TextDecoder } from "../shared/text-effects/TextDecoder";
import { useTextSize } from "../shared/hooks/useTextSize";

const EmailSchema = z
  .string()
  .email("Invalid email address")
  .transform((email) => email.trim().toLowerCase());

const EmailPage: FC<PageProps> = ({ title }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useRecoilState<string>(emailState);

  const hiddenRef = useRef<HTMLHeadingElement>(null);
  const { height: titleHeight } = useTextSize(hiddenRef);

  const onContinueButtonClick = () => {
    handleFormSubmit();
  };

  const handleEnter = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      btnRef.current?.click();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleEnter);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleEnter);
    };
  }, [handleEnter]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    resetError();
  }, [email]);

  const resetError = () => {
    setError(null);
  };

  const handleFormSubmit = async () => {
    resetError();

    try {
      EmailSchema.parse(email.trim());
      navigate("/child-details");
    } catch (e) {
      setError((e as ZodError).issues[0]?.message || "");
    }
  };

  return (
    <div className="w-full flex-1 items-center flex">
      <div className="flex flex-col w-full">
        <div
          className="whitespace-normal w-full relative"
          style={{ height: `${titleHeight}px` }}
        >
          <TextDecoder
            className="font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem] gap-5"
            delay={0}
            duration={500}
            animationType={"in"}
            text="Enter your email"
          />
          <div
            ref={hiddenRef}
            className="absolute opacity-0 top-0 pointer-events-none font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem]"
          >
            Enter your email
          </div>
        </div>

        <Input
          className="flex-1 mt-10"
          size="sm"
          type="email"
          label="Email address"
          value={email}
          onChange={setEmail}
          error={error}
        />

        <Button
          ref={btnRef}
          variant="filled"
          size="sm"
          type="submit"
          className="text-[1.2rem] mt-3 w-full sm:w-57.5"
          onClick={onContinueButtonClick}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default EmailPage;
